import React, { useState, useEffect, useMemo } from "react";

export default function CustomerTab({
  customers = [],
  onAdd,
  onUpdate,
  onDelete,
  isSuperuser = false,
  refreshCustomers,
}) {
  const [name, setName] = useState("");
  const [inn, setInn] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editInn, setEditInn] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (typeof refreshCustomers === "function" && customers.length === 0) {
      refreshCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.inn || "").toLowerCase().includes(q)
    );
  }, [customers, filter]);

  const handleAddSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError("");
    try {
      await onAdd({ name: name.trim(), inn: inn.trim() || null });
      setName("");
      setInn("");
      if (typeof refreshCustomers === "function") {
        await refreshCustomers();
      }
    } catch (err) {
      setError(err?.message || "Unable to add customer.");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (customer) => {
    setEditingId(customer.id);
    setEditName(customer.name || "");
    setEditInn(customer.inn || "");
    setError("");
    setConfirmDeleteId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditInn("");
  };

  const saveEdit = async (id) => {
    if (!editName.trim() || typeof onUpdate !== "function") return;
    setEditBusy(true);
    setError("");
    try {
      await onUpdate(id, {
        name: editName.trim(),
        inn: editInn.trim() || null,
      });
      cancelEdit();
      if (typeof refreshCustomers === "function") {
        await refreshCustomers();
      }
    } catch (err) {
      setError(err?.message || "Unable to update customer.");
    } finally {
      setEditBusy(false);
    }
  };

  const confirmDelete = async (id) => {
    if (typeof onDelete !== "function") return;
    setEditBusy(true);
    setError("");
    try {
      await onDelete(id);
      setConfirmDeleteId(null);
      if (typeof refreshCustomers === "function") {
        await refreshCustomers();
      }
    } catch (err) {
      setError(err?.message || "Unable to delete customer.");
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <div className="customer-tab">
      <form className="project-form" onSubmit={handleAddSubmit}>
        <div className="form-row">
          <div className="form-section">
            <label htmlFor="customer-name">Customer name</label>
            <input
              id="customer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Customer / Company name"
            />
          </div>

          <div className="form-section">
            <label htmlFor="customer-inn">
              INN / Tax ID <span className="label-optional">(optional)</span>
            </label>
            <input
              id="customer-inn"
              value={inn}
              onChange={(e) => setInn(e.target.value)}
              placeholder="Tax ID"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="primary" disabled={busy}>
            {busy ? "Adding…" : "Add customer"}
          </button>
        </div>

        {error && <div className="msg-error">{error}</div>}
      </form>

      {/* Customer list */}
      <div className="customer-list-section">
        <div className="customer-list-header">
          <h3>Customers ({customers.length})</h3>
          <input
            type="text"
            className="customer-filter"
            placeholder="Filter..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="customer-empty">
            {customers.length === 0 ? "No customers yet." : "No matches."}
          </div>
        ) : (
          <ul className="customer-list">
            {filtered.map((customer) => {
              const isEditing = editingId === customer.id;
              const isConfirming = confirmDeleteId === customer.id;

              return (
                <li key={customer.id} className={"customer-item" + (isEditing ? " is-editing" : "")}>
                  {isEditing ? (
                    <div className="customer-edit-form">
                      <input
                        className="customer-edit-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Name"
                      />
                      <input
                        className="customer-edit-input"
                        value={editInn}
                        onChange={(e) => setEditInn(e.target.value)}
                        placeholder="INN"
                      />
                      <div className="customer-actions">
                        <button
                          type="button"
                          className="primary"
                          onClick={() => saveEdit(customer.id)}
                          disabled={editBusy || !editName.trim()}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="secondary"
                          onClick={cancelEdit}
                          disabled={editBusy}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="customer-info">
                        <div className="customer-name">{customer.name}</div>
                        {customer.inn && (
                          <div className="customer-inn">INN: {customer.inn}</div>
                        )}
                      </div>

                      {isSuperuser && (
                        <div className="customer-actions">
                          {isConfirming ? (
                            <>
                              <button
                                type="button"
                                className="danger"
                                onClick={() => confirmDelete(customer.id)}
                                disabled={editBusy}
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                className="secondary"
                                onClick={() => setConfirmDeleteId(null)}
                                disabled={editBusy}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => startEdit(customer)}
                                title="Edit"
                              >
                                ✎
                              </button>
                              <button
                                type="button"
                                className="icon-btn icon-btn--danger"
                                onClick={() => setConfirmDeleteId(customer.id)}
                                title="Delete"
                              >
                                🗑
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
